import {
  PHOTON_TEST_APP_ID, PHOTON_APP_VERSION, PHOTON_CONNECTION_TIMEOUT, PHOTON_REGION,
  WAIT_JOIN_ROOM_SECONDS, WAIT_NEW_ROOM_IN_LOBBY_SECONDS,
  SEPARATOR_PRIVATE_ROOMS, SEPARATOR_PUBLIC_ROOMS,
  SEPARATOR_REMATCH_ROOM_COUNT,
} from '../../config/config';
import { IS_LOCAL } from '../../config/env';
import { newColorMaster } from '../game/boardInit';
import { nap } from '../utils';
import { photonApi, resetClient } from './instance';

export const PhotonApi = window.Photon;
window.Photon = undefined;


// PHOTON CLIENT STATE
export const clientState = {
  isConnected: false,
  photonState: null,
  inRoom: false,
  playing: false,
};

// ROOMS
// Note: filled only if you're in the lobby and someone creates it (instead of joining yours - eg. private)
let availableRooms = [];
let roomsToIgnore = JSON.parse(sessionStorage.getItem('roomsToIgnore')) || [];
let sameSessionSamePlayersCount = 0;

// PHOTON STATS
export const photonStats = {
  playersInLobby: -1, playersPlaying: -1, playersOnline: -1,
};

// ONLINE STATUS (local player and opponent)
let tsLastPingOpponent = null;
export const getOpponentLastPing = () => tsLastPingOpponent;


// ---------- PHOTON CLIENT INIT and all its HANDLERS -------------

export const initializePhotonClient = ({ appData, addMsg }) => {
  if (photonApi.client) return photonApi.client;

  // ## PHOTON CLIENT INIT
  photonApi.client = new PhotonApi.LoadBalancing.LoadBalancingClient(
    PhotonApi.ConnectionProtocol.Wss,
    PHOTON_TEST_APP_ID,
    PHOTON_APP_VERSION
  );
  clientState.inRoom = false;
  clientState.playing = false;
  // Set the player's custom properties including the desired name
  photonApi.client.myActor().setCustomProperties({ playerName: appData.loggedInUser });
  
  // ## SET ALL EVENT CALLBACKS

  const { loggedInUser: currentUser, isInviteMatch, players = {}, room: lastRoom = {} } = appData;

  // state change
  photonApi.client.onStateChange = (state) => {
    const photonState = PhotonApi.LoadBalancing.LoadBalancingClient.StateToName(state);
    addMsg('Photon state changed: ' + photonState, { id: 'client:event', dev: true });
    if (state === PhotonApi.LoadBalancing.LoadBalancingClient.State.JoinedLobby) {
      addMsg(`${currentUser} landed in the lobby`, { id: 'client:event' });
    }
    // track connection
    if (
      state === PhotonApi.LoadBalancing.LoadBalancingClient.State.ConnectedToMaster ||
      state === PhotonApi.LoadBalancing.LoadBalancingClient.State.JoinedLobby ||
      state === PhotonApi.LoadBalancing.LoadBalancingClient.State.ConnectingToGameserver ||
      state === PhotonApi.LoadBalancing.LoadBalancingClient.State.ConnectedToGameserver ||
      state === PhotonApi.LoadBalancing.LoadBalancingClient.State.Joined
    ) {
      clientState.isConnected = true;
    } else {
      clientState.isConnected = false;
    }
    clientState.photonState = photonState;
  };

  // errors
  photonApi.client.onError = (errorCode, errorMsg) => {
    console.error('photon-error:', { errorCode, errorMsg });
    addMsg(`Server error`, { id: 'client:event', devData: `${errorCode} - ${errorMsg}` });
    // clientState.inRoom = false; // right???
    // clientState.playing = false; // right???
  };

  // room list changes
  photonApi.client.onRoomListUpdate = (rooms) => { // works if someone creates room while im in lobby!
    availableRooms = rooms;
    addMsg('Someone created another room', { id: 'client:event', dev: true, devData: `Room List: ${JSON.stringify(rooms)}` });
    if (rooms.length && !clientState.inRoom) {
      const roomName = rooms[0].name;
      if (!roomsToIgnore.includes(roomName)) {
        joinTargetRoom(roomName);
      }
    }
  };

  // photon stats
  photonApi.client.onAppStats = (errorCode, errorMsg, stats) => {
    IS_LOCAL && console.log('APP STATS EVENT::::', { errorCode, errorMsg, stats });
    if (errorCode === 0) {
      const playersInLobby = stats.masterPeerCount || 0; // Players in lobby (not in game)
      const playersPlaying = stats.gameCount || 0; // Players currently playing in games
      const playersOnline = stats.peerCount || 0; // Total connections (lobby + playing)
      photonStats.playersInLobby = playersInLobby;
      photonStats.playersPlaying = playersPlaying;
      photonStats.playersOnline = playersOnline;
    } else {
      console.error(`Error receiving stats: ${errorMsg} (${errorCode})`);
      photonStats.playersInLobby = -4;
      photonStats.playersPlaying = -4;
      photonStats.playersOnline = -4;
    }
  };

  // disconnected
  photonApi.client.onDisconnected = () => {
    addMsg('Client disconnected.', { id: 'client:event', dev: true });
    resetClient();
    photonStats.playersInLobby = -5;
    photonStats.playersPlaying = -5;
    photonStats.playersOnline = -5;

    // clientState.inRoom = false;
    // clientState.playing = false;
    // TODO: reconnect @@ @@
  };

  // handlers for join/leave lobby/room
  photonApi.client.onJoinRoom = () => {
    const photonRoom = photonApi.client?.myRoom();
    const roomName = photonRoom.name;
    // AUTO-KICKs
    // 1. if I landed in a private room and mine was not an invite match: auto-kick
    const publicPlayerInPrivateRoom = !isInviteMatch && roomName.includes(SEPARATOR_PRIVATE_ROOMS);
    // 2. if I landed in my previous room: auto-kick and the timer will have me join another room
    const { roomID: lastRoomName } = lastRoom;
    const isLastMatchRoom = roomName === lastRoomName;
    // 3. if I landed in a game-over room (only one player left): auto-kick
    const isGameOverStaleRoom = photonRoom.getCustomProperty('gameOver');
    // 4. If I landed in a room where one player abandoned mid-game: auto-kick
    const roomPlayers = photonRoom.getCustomProperty('activePlayers') || [];
    const isHalfAbandonedRoom = roomPlayers.length === 1 && !roomPlayers.includes(currentUser);
    addMsg(`Auto-kick checks:`, { id: 'client:event', dev: true,
        devData: JSON.stringify({ publicPlayerInPrivateRoom, isLastMatchRoom, isGameOverStaleRoom, isHalfAbandonedRoom }),
      }
    );
    if (publicPlayerInPrivateRoom || isLastMatchRoom || isGameOverStaleRoom || isHalfAbandonedRoom) {
      IS_LOCAL && console.log(`Auto-kicked from room ${roomName}`,
        { publicPlayerInPrivateRoom, isLastMatchRoom, isGameOverStaleRoom, isHalfAbandonedRoom });
      leaveCurrentRoom();
      roomsToIgnore.push(roomName);
      sessionStorage.setItem('roomsToIgnore', JSON.stringify(roomsToIgnore));
      return;
    }
    // other room props
    const masterName = photonRoom.getCustomProperty('masterName');
    const masterColor = photonRoom.getCustomProperty('masterColor');
    IS_LOCAL && console.log('[mastercolor] found on room join:', masterColor);
    addMsg(`Joined room ${roomName}. Master: ${masterName} (${masterColor}). Calling Handlers..`, { id: 'client:event', dev: true });
    clientState.inRoom = true;
    roomsToIgnore.push(roomName);
    photonApi.eventHandlers.onJoinRoom && photonApi.eventHandlers.onJoinRoom(({ roomName, masterName, masterColor }));
  };
  photonApi.client.onJoinLobby = () => {
    // I belive this is not working, no log.
    addMsg('Joined Lobby', { id: 'client:event' });
  };
  photonApi.client.onLeaveLobby = () => {
    addMsg('Left Lobby', { id: 'client:event' });
  };
  photonApi.client.onLeaveRoom = () => {
    addMsg('Left Room (Back to Lobby)', { id: 'client:event' });
    clientState.inRoom = false;
    clientState.playing = false;
  };
  photonApi.client.onCreatedRoom = () => {
    addMsg('Left Room (Back to Lobby)', { id: 'client:event', dev: true });
  };

  // receive photon events
  // {{ NETWORK DATA HERE }}
  photonApi.client.onEvent = (eventCode, content = {}) => {
    const isOpponentPing = content.type === 'PING' && content.from !== currentUser;
    !isOpponentPing && addMsg(`Photon event! ${JSON.stringify({ eventCode, content })}`, { id: 'client:event', dev: true });
    if (isOpponentPing) {
      tsLastPingOpponent = Date.now();
    }
    photonApi.eventHandlers.onEvent && photonApi.eventHandlers.onEvent(eventCode, content);
  };

  // join fail
  photonApi.client.onJoinRandomRoomFailed = (maybeErr) => {
    addMsg('Joining random room failed, creating one..', { id: 'client:event', devData: JSON.stringify(maybeErr) });
    clientState.inRoom = false;
    photonApi.eventHandlers.onJoinRoomFailed && photonApi.eventHandlers.onJoinRoomFailed();
    photonClientConnected()
      .then(({ isTimeout } = {}) => {
        if (isTimeout) {
          addMsg('Connectivity issues after failing to join a match. Please try again.', { id: 'client:event' });
          return;
        }
        if (isInviteMatch) {
          createRoom({ isInviteMatch, players, addMsg, currentUser });
        } else {
          createRoom({ addMsg, currentUser });
        }
      })
      .catch((err) => console.error('caught in onJoinRandomRoomFailed room creation:', err));
  };

  // PLAYER JOINED (didnt see this called)
  photonApi.client.onPlayerJoin = (player) => {
    const playerName = (player.getName && player.getName()) || player.customProperties?.playerName;
    addMsg(`Player ${playerName} joined the room`, { id: 'client:event' });
    photonApi.eventHandlers.onPlayerJoin && photonApi.eventHandlers.onPlayerJoin(playerName);
  };

  return photonApi.client;
};


// --------- CONNECTIVITY UTILS -----------

// Check if the client is connected
const isClientConnected = () => photonApi.client?.isConnectedToMaster();

// Retry connection if not connected
const photonClientConnected = () => new Promise(async (resolve) => {
  if (!isClientConnected()) {
    try {
      photonApi.client?.connectToRegionMaster(PHOTON_REGION);
    } catch (err) {
      console.error('Failed to connect to master', err, '\nTrying once more...');
      photonApi.client?.connectToRegionMaster(PHOTON_REGION);
    }
    await nap(1000);
  }
  if (!isClientConnected()) {
    let eventuallyConnected = false;
    const ogHandler = photonApi.client?.onStateChange;
    if (!photonApi.cli) return resolve({});
    photonApi.client.onStateChange = (state) => {
      ogHandler && ogHandler(state);
      if (state === PhotonApi.LoadBalancing.LoadBalancingClient.State.ConnectedToMaster) {
        eventuallyConnected = true;
        resolve({});
      }
    };
    setTimeout(() => !eventuallyConnected && resolve({ isTimeout: true }), PHOTON_CONNECTION_TIMEOUT); // timeout
  } else {
    resolve({});
  }
});


// --------- ENTRY POINT FOR LOBBY COMPONENT --------

// Connect to the Photon room based on the game setup
export const connectToPhotonRoom = async ({ appData, addMsg }) => {
  // INIT CLIENT
  initializePhotonClient({ appData, addMsg });
  // Connect to the Master server if not already connected
  const { isTimeout } = await photonClientConnected();
  if (isTimeout) {
    addMsg('Connection Timeout. Please try again', { id: 'client:connect' });
    return;
  }
  // JOIN/CREATE ROOM
  const { loggedInUser: currentUser, players, room, isInviteMatch } = appData;
  const { roomID: previousMatchRoom } = room;
  if (isInviteMatch) {
    sameSessionSamePlayersCount++;
    const privateRoomName = `${players.player1}${SEPARATOR_PRIVATE_ROOMS}${players.player2}${
      SEPARATOR_REMATCH_ROOM_COUNT}${sameSessionSamePlayersCount}`;
    joinTargetRoom(privateRoomName);
    // if room doesnt exists yet, create it..
    setTimeout(
      () => !clientState.inRoom && createRoom({ isInviteMatch, players, addMsg, currentUser }),
      WAIT_JOIN_ROOM_SECONDS * 1000,
    );
  } else {
    addMsg('Getting list of public rooms then joining random or creating one..', { id: 'client:connect', dev: true });
    if (!previousMatchRoom) {
      // FIRST MATCH
      // NOTE: used by opponent to join existing room!
      joinRandomRoom(); // could join an invite room (and auto-kicked)
      // Timers
      const waitTime = WAIT_JOIN_ROOM_SECONDS * 1000;
      const firstMatchIntervalId = setInterval(() => {
        // NOTE: used for master player to create the room
        if (clientState.inRoom) {
          clearInterval(firstMatchIntervalId);
          return;
        }
        joinRandomPublicMatchOrCreate({ addMsg, currentUser });
      }, waitTime);
      // Fallback, try to join other random room instead of always creating one
      const joinRandomIntervalId = setInterval(() => {
        if (!clientState.inRoom || (clientState.inRoom && !clientState.playing)) joinRandomRoom();
        else clearInterval(joinRandomIntervalId);
      }, waitTime * 1.5);
    } else {
      // BACK TO LOBBY
      // Note: dont straight use joinRandomRoom or you just re-join the old room still open
      joinRandomPublicMatchOrCreate({ addMsg, currentUser, previousMatchRoom });
      // Timers
      const waitTime = (WAIT_NEW_ROOM_IN_LOBBY_SECONDS * 1000) * (Math.random() + 1);
      const backToLobbyIntervalId = setInterval(() => {
        if (clientState.playing) {
          clearInterval(backToLobbyIntervalId);
          return;
        }
        if (clientState.inRoom) leaveCurrentRoom();
        joinRandomPublicMatchOrCreate({ addMsg, currentUser, previousMatchRoom });
      }, waitTime);
      // Fallback, try to join other random room instead of always creating one
      const joinRandomIntervalId = setInterval(() => {
        if (clientState.playing) {
          clearInterval(joinRandomIntervalId);
          return;
        }
        if (clientState.inRoom && !clientState.playing) { // stuck in my own created room
          leaveCurrentRoom();
          joinRandomRoom();
        }
      }, waitTime * 1.5);
    }
  }
};

// JOIN RANDOM ROOM OR CREATE
// Public: 1252197334_cribibo, Invite: gaottantacinque:cribbio
const joinRandomPublicMatchOrCreate = ({ addMsg, currentUser, previousMatchRoom }) => {
  addMsg('Total rooms created while I was in the lobby: ' + availableRooms.length, { id: 'client:join/create', dev: true });
  // filter rooms to make sure they're not invite-only rooms and not the previous room
  const publicRooms = availableRooms.filter((room = {}) => {
    const roomName = room.name || '';
    const [tsStr, creator] = roomName.split(SEPARATOR_PUBLIC_ROOMS);
    const isPublicRoom = creator && parseInt(tsStr) > 0;
    const isPreviousMatchRoom = roomName === previousMatchRoom;
    const isPreviouslyAutoKickedRoom = roomsToIgnore.includes(roomName);
    return isPublicRoom && !isPreviousMatchRoom && !isPreviouslyAutoKickedRoom;
  });
  if (publicRooms.length === 0) {
    addMsg(`No public rooms (${publicRooms.length}), creating a new one...`, { id: 'client:join/create', dev: true });
    createRoom({ addMsg, currentUser });
  } else {
    const randomRoom = publicRooms[Math.floor(Math.random() * publicRooms.length)];
    joinTargetRoom(randomRoom.name);
    addMsg(`Found ${availableRooms.length} rooms, ${publicRooms.length} of which public. Joining public room ${randomRoom.name}`, { id: 'client:join/create' });
  }
};

// CREATE ROOM
const createRoom = async ({ isInviteMatch, players, addMsg = () => {}, currentUser } = {}) => {
  // Check in Lobby and not still connected to a previous game server
  const photonCurrentState = photonApi.client?.state();
  if (photonCurrentState === PhotonApi.LoadBalancing.LoadBalancingClient.State.Joined) {
    addMsg('Leaving current room', { id: 'client:leaveRoom', dev: true });
    leaveCurrentRoom();
    await nap(1000);
  }
  // Check connected
  const { isTimeout } = await photonClientConnected();
  if (isTimeout) {
    addMsg('Connection Timeout creating room. Please try again', { id: 'client:connect' });
    return;
  }
  // Decide master color
  const masterColor = newColorMaster({ isInviteMatch });
  IS_LOCAL && console.log('[mastercolor] set during room creation:', masterColor);
  // Config room
  const roomOptions = {
    isVisible: true, // Ensure the room is visible in the lobby
    isOpen: true, // Ensure the room is open for joining
    maxPlayers: 2, // Set the maximum number of players - no need to kick player if already 2 players playing in a room
    customGameProperties: {
      masterName: currentUser,
      masterColor,
      gameOver: false,
      activePlayers: [],
    },
    propsListedInLobby: ['masterName', 'masterColor', 'gameOver', 'activePlayers'],
  };
  let finalRoomName;
  if (isInviteMatch) {
    finalRoomName = `${players.player1}${SEPARATOR_PRIVATE_ROOMS}${players.player2}${
      SEPARATOR_REMATCH_ROOM_COUNT}${sameSessionSamePlayersCount}`;
  } else {
    finalRoomName = `${Date.now()}${SEPARATOR_PUBLIC_ROOMS}${currentUser}`;
  }
  // Dupe check
  const isDupeRoom = availableRooms.find(room => room.name === finalRoomName);
  if (isDupeRoom) return; // just join that room created by another user
  // Create
  addMsg(`Creating room: ${finalRoomName} with options:`, { id: 'client:createroom', dev: true, devData: JSON.stringify(roomOptions) });
  try {
    photonApi.client?.createRoom(
      finalRoomName,
      roomOptions,
      { lobbyName: 'cryptochess lobby', lobbyType: PhotonApi.LoadBalancing.Constants.LobbyType.Default },
    );
  } catch (err) { console.error('caught error creating custom room:', err); }
};


// SET PHOTON EVENT HANDLERS
export const setPhotonEventHandlers = (handlers) => {
  photonApi.eventHandlers = handlers;
};

// JOIN RANDOM ROOM
const joinRandomRoom = () => {
  try {
    photonApi.client?.joinRandomRoom();
  } catch (err) { console.error('Error joining random room:', err); }
};

// JOIN TARGET ROOM
const joinTargetRoom = (roomName) => {
  try {
    photonApi.client?.joinRoom(roomName);
  } catch (err) { console.error('Error joining target room:', roomName, err); }
};

// GET ROOM PLAYERS
export const getMyRoomPlayers = () => {
  return photonApi.client?.myRoomActorsArray().map((actor) => {
    const actorName = (actor.getName && actor.getName()) || actor.customProperties?.playerName;
    return actorName;
  });
};

// SEND PHOTON EVENT
export const sendPhotonEvent = (eventCode, content) => {
  photonApi.client?.raiseEvent(eventCode, content);
};

// ADD ACTIVE PLAYERS TO ROOM (for auto-kick)
export const masterAddRoomActivePlayers = (arr = []) => {
  try {
    const myActorNr = photonApi.client?.myActor()?.actorNr;
    const masterActorNr = photonApi.client?.myRoom()?.masterClientId;
    if (myActorNr === masterActorNr) {
      photonApi.client?.myRoom()?.setCustomProperties({ activePlayers: arr });
    }
  } catch (err) { console.error('Error master setting room active players:', err); }
};

// MARK ROOM AS GAME OVER
export const masterMarkRoomAsGameOver = () => {
  try {
    const myActorNr = photonApi.client?.myActor()?.actorNr;
    const photonRoom = photonApi.client?.myRoom();
    const masterActorNr = photonRoom?.masterClientId;
    if (myActorNr === masterActorNr) {
      photonApi.client?.myRoom()?.setCustomProperties({ gameOver: true });
      IS_LOCAL && console.log('Set room as game over - updated properties:', photonRoom?.getCustomProperties());
    }
  } catch (err) { console.error('Error master marking the room as game over:', err); }
};

// LEAVE ROOM
export const leaveCurrentRoom = () => {
  try {
    IS_LOCAL && console.log('Leaving current room...');
    if (photonApi.client) photonApi.client.leaveRoom(); // dont add inRoom check, photon status can be joined on refresh
  } catch (err) { console.error('Error leaving the room:', err); }
};

// DISCONNECT CLIENT
export const disconnectClient = () => {
  try {
    if (photonApi.client) photonApi.client.disconnect();
  } catch (err) { console.error('Error disconnecting:', err); }
  resetClient();
};
